import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { StreamChat } from 'stream-chat';
import type { ChatTokenResponse } from '@chat-emp/shared';

import { apiFetch } from '../lib/api.js';
import { track } from '../lib/analytics.js';
import { useSession } from './SessionProvider.js';

type Status = 'idle' | 'connecting' | 'ready' | 'error';

interface StreamState {
  client: StreamChat | null;
  status: Status;
  /** Run the connect again; the gate calls it after a failed connect. */
  retry: () => void;
}

const StreamContext = createContext<StreamState>({ client: null, status: 'idle', retry: () => {} });

/** The shared connected Stream client for the seam (null until connected). */
export function useStreamClient(): StreamState {
  return useContext(StreamContext);
}

/**
 * Connects to Stream only while `connect` is true and the session is ready, and
 * disconnects on teardown — the same lazy lifecycle the old `useChatSession`
 * Phase 2 had, so `/chat` passes `connect` always and the widget passes it only
 * while the panel is open. `connectUser` is what Stream bills (MAU + concurrent
 * connection), so a closed widget connects nothing.
 *
 * The api key + user id + token all come from the BFF at runtime
 * (`GET /api/chat/token`); the user id is server-derived — the client never
 * supplies one.
 */
export function StreamProvider({ connect, children }: { connect: boolean; children: ReactNode }) {
  const { sessionReady } = useSession();
  const [client, setClient] = useState<StreamChat | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!connect || !sessionReady) return;

    let cancelled = false;
    let connected: StreamChat | null = null;

    async function connectStream() {
      // Distinguishes which step failed for analytics: the BFF token fetch vs the
      // Stream connect. Both still surface the same 'error' status to the UI.
      let tokenFetched = false;
      try {
        setStatus('connecting');
        const { apiKey, userId } = await apiFetch<ChatTokenResponse>('/api/chat/token');
        tokenFetched = true;
        if (cancelled) return;
        // Long-poll over HTTPS when the socket cannot connect: office networks block
        // WebSockets. Proven on the dev Stream app (AI-3493); prod needs Stream to allow it.
        connected = StreamChat.getInstance(apiKey, { enableWSFallback: true });
        // A provider, not the token string that call also returned. Stream
        // tokens now expire with the session, and the SDK refreshes an expired
        // one only when the token is NOT static — `!tokenManager.isStatic()`
        // guards every TOKEN_EXPIRED path. Handed a bare string it would drop
        // the connection instead, and nothing would rebuild it: this effect
        // depends on `connect` and `sessionReady`, neither of which changes
        // again. The extra fetch is one round trip at connect, once.
        await connected.connectUser(
          { id: userId },
          async () => (await apiFetch<ChatTokenResponse>('/api/chat/token')).token,
        );
        if (cancelled) {
          void connected.disconnectUser();
          return;
        }
        setClient(connected);
        setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          track(tokenFetched ? 'chat_init_failed' : 'chat_token_fetch_failed', {
            reason: String(err instanceof Error ? err.message : err).slice(0, 120),
          });
          setStatus('error');
        }
      }
    }

    void connectStream();

    return () => {
      cancelled = true;
      setClient(null);
      setStatus('idle');
      void connected?.disconnectUser();
    };
  }, [connect, sessionReady, attempt]);

  return (
    <StreamContext.Provider value={{ client, status, retry }}>{children}</StreamContext.Provider>
  );
}
